// src/utils/downloadChunkedBlob.ts
import { db } from "../idb/chunk-db";
import { IChunkRow } from "../types/db";

/** Simple file name helper. */
export function generateRecordingFilename(
  videoGuid: string,
  container?: "webm" | "mp4"
) {
  const ext = container ?? "webm";
  const date = new Date().toISOString().replace(/[:.]/g, "-");
  return `RecordingStudio-${date}.${ext}`;
}

/** Basic browser download for a Blob. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Fetch chunks for a recording in order, and do some sanity checks. */
export async function getOrderedChunks(videoGuid: string) {
  const rows = await db.chunks
    .where("videoGuid")
    .equals(videoGuid)
    .sortBy("chunkNo");

  if (!rows.length) throw new Error("No chunks found for videoGuid.");

  // Check monotonic ordering and continuity starting at 1
  rows.forEach((r, i) => {
    const expected = i + 1;
    if (r.chunkNo !== expected) {
      throw new Error(
        `Missing or out-of-order chunk: expected #${expected}, got #${r.chunkNo}`
      );
    }
  });

  const hasLast = rows[rows.length - 1]?.isLast === true;
  return { rows, hasLast };
}

/**
 * Download a recording assembled from IndexedDB chunks.
 * - Prefers streaming to disk (File System Access API) if available.
 * - Falls back to Blob->a[download].
 * - Optionally cleans up chunks afterwards.
 */
export async function downloadChunkedBlob(
  videoGuid: string,
  opts?: {
    filename?: string;
    containerHint?: "webm" | "mp4"; // used only if blob.type is empty
    cleanupAfter?: boolean;
    onProgress?: (writtenBytes: number, totalBytes: number) => void;
  }
) {
  const { rows, hasLast } = await getOrderedChunks(videoGuid);
  if (!hasLast) {
    // Not strictly required, but helpful to avoid partial downloads.
    console.warn(
      "downloadChunkedBlob: final chunk not marked isLast=true yet."
    );
  }

  const totalBytes = rows.reduce((sum, r) => sum + (r.size || (r.blob instanceof Blob ? r.blob.size : (r.blob as ArrayBuffer).byteLength)), 0);
  const firstType = rows[0].blob instanceof Blob ? rows[0].blob.type : "video/mp4"; // should be "video/webm" or "video/mp4"
  const container =
    (firstType.includes("mp4")
      ? "mp4"
      : firstType.includes("webm")
      ? "webm"
      : undefined) ??
    opts?.containerHint ??
    "webm";
  const filename =
    opts?.filename ?? generateRecordingFilename(videoGuid, container);

  // If the File System Access API is available, stream directly to disk:
  const canStreamToDisk =
    typeof (window as any).showSaveFilePicker === "function";

  if (canStreamToDisk) {
    await streamToDisk(
      rows,
      filename,
      firstType || `video/${container}`,
      totalBytes,
      opts?.onProgress
    );
  } else {
    // Fallback: single big Blob (OK for moderately sized recordings)
    const blob = new Blob(
      rows.map((r) => r.blob instanceof Blob ? r.blob : new Blob([r.blob as ArrayBuffer])),
      { type: firstType || `video/${container}` }
    );
    downloadBlob(blob, filename);
  }

  if (opts?.cleanupAfter) {
    await db.chunks.where("videoGuid").equals(videoGuid).delete();
  }
}

/** Stream chunks to disk via File System Access API (no giant in-memory blob). */
async function streamToDisk(
  rows: IChunkRow[],
  filename: string,
  mime: string,
  totalBytes: number,
  onProgress?: (written: number, total: number) => void
) {
  // @ts-expect-error: TS may not include FS Access types by default.
  const handle = await window.showSaveFilePicker({
    suggestedName: filename,
    types: [
      {
        description: "Video",
        accept: { [mime || "video/*"]: [".webm", ".mp4"] },
      },
    ],
  });

  const writable = await (handle as any).createWritable();

  let written = 0;
  for (const r of rows) {
    const chunkBlob = r.blob instanceof Blob ? r.blob : new Blob([r.blob as ArrayBuffer]);
    await writable.write(chunkBlob);
    written += chunkBlob.size;
    onProgress?.(written, totalBytes);
  }

  await writable.close();
}
